import React, { useContext, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  AppBar,
  Avatar,
  Tooltip,
  Toolbar,
  Popover,
  Box,
  Drawer,
  Divider,
  Badge,
  ListItemText,
  ListItem,
  Typography,
  List,
  IconButton,
  badgeClasses,
  popoverClasses,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useNavigate } from "react-router-dom";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import logo from "./nav-logo.svg";
import { AccountContext } from "../../store/AccountContext";

import NotificationsIcon from "@mui/icons-material/Notifications";
import { OverviewContext } from "../../store/OverviewContext";

import SearchModalIconButton from "../Search/trigger/SearchModalIconButton";
import { MEMBER_STATUS_STANDARD, MEMBER_STATUS_TRIAL } from "@booyaltd/core";

import WarningRoundedIcon from "@mui/icons-material/WarningRounded";

/**
 * PM5 T1 (chrome, batch "header") — converted from MUI v4 + `makeStyles` to
 * MUI v5+ (`@mui/material`) + `styled`/`sx`.
 * See docs/mui-conversion-guide.md; acceptance bar is visual parity.
 *
 * NOT PORTED — nineteen `useStyles` keys that no element in this file ever
 * referenced: `drawerPaper`, `menuButton`, `title`, `dropdownMenu`, `menuIcon`,
 * `buttonIconLarge`, `profileButtonIcon`, `search`, `searchButtonIcon`,
 * `inputRoot`, `inputInput`, `sectionDesktop`, `sectionMobile`, `profileName`,
 * `profileMenu`, `mobileAccountIcon`, `mobileIconButton`,
 * `mobileNotificationBadge` and `notificationDate`. They emitted stylesheet
 * rules but were never applied to an element, so dropping them is provably
 * zero-visual-change. `search` was this file's only consumer of v4's `fade()`,
 * which is why no `alpha()` import replaces it.
 */

/* ---------------------------------------------------------------------- *
 * styled() — host elements that carry a fixed, reused style
 * ---------------------------------------------------------------------- */

// was: useStyles().grow — the flex spacer <div>s
const Grow = styled("div")({
  flexGrow: 1,
});

// was: useStyles().logo
const Logo = styled("img")({
  width: "130px",
  height: "auto",
  padding: "10px 0",
});

// was: useStyles().notificationUnreadIcon — a bare <div>, so no `sx` prop
const NotificationUnreadIcon = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "24px",
  right: "20px",
  height: "12px",
  width: "12px",
  borderRadius: "50%",
  backgroundColor: theme.palette.background.ffGreen,
}));

// was: useStyles().mobileNavListItem — applied to react-router <Link>s, which
// have no `sx` prop, so these are styled() wrappers rather than sx objects.
const MobileNavListItemLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.lightgray,
  textDecoration: "none",
  fontSize: "1.4rem",
  fontWeight: 600,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

// was: useStyles().mobileNavListItemSub — the same rule is applied to <Link>s,
// to a bare <a>, and (for "Logout") to a MUI <Typography>. One style function,
// three hosts: styled() for the two that have no `sx` prop, and the function
// form of `sx` for the Typography.
const mobileNavListItemSubStyles = (theme) => ({
  color: theme.palette.common.lightgray,
  textDecoration: "none",
  fontWeight: 400,
  "&:hover": {
    color: theme.palette.background.ffGreen,
    cursor: "pointer",
  },
});

const MobileNavListItemSubLink = styled(Link)(({ theme }) =>
  mobileNavListItemSubStyles(theme),
);

const MobileNavListItemSubAnchor = styled("a")(({ theme }) =>
  mobileNavListItemSubStyles(theme),
);

/* ---------------------------------------------------------------------- *
 * sx — one-off / repeated styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().iconContainer
const iconContainerSx = {
  padding: "0px 10px",
};

// was: useStyles().buttonIcon. The v4 rule's `@media (min-width:960px)` block
// restates the same font size, so it is inert; kept (spelled as the pinned `md`
// breakpoint) so the two rule sets stay diff-able line for line.
const buttonIconSx = (theme) => ({
  fontSize: "1.6rem",
  borderRadius: "50%",
  margin: "0",
  color: theme.palette.common.lightgray,
  cursor: "pointer",
  "&:hover": {
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
  },
});

// was: useStyles().profileIconContainer
const profileIconContainerSx = {
  fontSize: "2.8rem",
  padding: "0 0 0 10px",
  color: "common.lightgray",
  cursor: "pointer",
};

// was: useStyles().notificationIconContainer — v4's `theme.spacing(0.5, 2, 0, 2)`
// / `theme.spacing(0, 1.5)` shorthands re-expressed as the equivalent sx
// spacing multiples (same 8px unit, so identical CSS).
const notificationIconContainerSx = {
  mt: 0.5,
  mr: 2,
  mb: 0,
  ml: 2,
  py: 0,
  px: 1.5,
};

// was: useStyles().notificationButtonIcon
const notificationButtonIconSx = {
  fontSize: "1.8rem",
  borderRadius: "50%",
  color: "common.lightgray",
  cursor: "pointer",
  my: 0.4,
  mx: 0,
  "&:hover": {
    color: "primary.main",
  },
};

// was: useStyles().notificationBadge.
// ♦ The v4 rule targeted the global class `.MuiBadge-anchorOriginTopRightRectangle`.
// Two things move at once on the v5+ side: the `FfV5` class prefix (so a
// hand-written `.Mui…` string would stop matching), and the slot's own rename —
// v4 spelled the overlap "Rectangle", v5+ spells it "Rectangular". Reading the
// name off `badgeClasses` covers both.
const notificationBadgeSx = {
  [`& .${badgeClasses.anchorOriginTopRightRectangular}`]: {
    top: "5px",
    right: "5px",
  },
};

// was: useStyles().sideNav — applied via v4's `classes={{ paper: … }}` on the
// Drawer. v9 exposes the same element through `slotProps.paper`.
const sideNavPaperSx = (theme) => ({
  width: "100%",
  backgroundColor: theme.palette.background.dark,
  [theme.breakpoints.up("sm")]: {
    maxWidth: "400px",
    minWidth: "300px",
  },
});

// was: useStyles().mobileNavCloseIcon
const mobileNavCloseIconSx = {
  color: "common.lightgray",
  fontSize: "2rem",
};

// was: useStyles().mobileProfileButtonIcon
const mobileProfileButtonIconSx = {
  fontSize: "3.2rem",
  paddingRight: "10px",
  color: "#4b4b4b",
};

// was: useStyles().mobileProfileName
const mobileProfileNameSx = {
  fontSize: "1.4rem",
  fontWeight: 500,
  color: "common.gray",
  lineHeight: "1.6rem",
};

// was: useStyles().mobileNavListDivider
const mobileNavListDividerSx = {
  height: "2px",
  marginLeft: 2,
};

// was: useStyles().notificationsContainer.
// ♦ `.MuiPopover-paper` reaches into the Popover this file renders — once the
// Popover is a v5+ component its paper is `.FfV5Popover-paper`, so the literal
// string would silently stop matching. Read off `popoverClasses` instead.
const notificationsContainerSx = (theme) => ({
  backgroundColor: "rgba(10,10,10,0.8)",
  [`& .${popoverClasses.paper}`]: {
    backgroundColor: "#383838",
    borderRadius: 0,
    marginTop: "10px",
    [theme.breakpoints.up("sm")]: {
      marginTop: "0px",
    },
  },
});

// was: useStyles().notificationsList. The nested `& p` / `& a` / `& a li` keys
// all contain `&`, so JSS's nested plugin did emit them in v4; carried over
// verbatim.
const notificationsListSx = (theme) => ({
  backgroundColor: "#383838",
  borderRadius: "0px",
  // maxWidth: "100%",
  paddingTop: "0px",
  paddingBottom: "0px",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "300px",
  },
  "& p": {
    lineHeight: "1.1rem",
    width: "88%",
  },
  "& a": {
    textDecoration: "none !important",
    "& li": {
      color: "#e9e9e9",
      "&:hover": {
        textDecoration: "none",
        backgroundColor: "#444444",
      },
    },
  },
});

// was: useStyles().notificationWrapper
const notificationWrapperSx = {
  borderBottom: "1px solid #181818",
  py: 1,
  px: 2,
  position: "relative",
};

// was: useStyles().notificationTitle
const notificationTitleSx = (theme) => ({
  fontSize: "1.1rem",
  lineHeight: "1.3rem",
  fontWeight: 500,
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.up("sm")]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
});

// was: useStyles().notificationDesc
const notificationDescSx = {
  fontSize: "0.9rem",
  lineHeight: "1rem",
  marginBottom: 1,
  textOverflow: "ellipsis",
  overflow: "hidden",
  display: "block",
  whiteSpace: "nowrap",
  color: "#a9a9a9",
};

// was: useStyles().noNotificationsIcon
const noNotificationsIconSx = {
  paddingTop: "0.1em",
  height: "1.8em",
  width: "1.8em",
};

// was: useStyles().notificationsViewAll
const notificationsViewAllSx = (theme) => ({
  textAlign: "center",
  textTransform: "uppercase",
  borderBottom: "1px solid #181818",
  color: "#a9a9a9",
  textDecoration: "none",
  "& span": {
    fontWeight: "600",
    fontSize: "0.8rem",
  },
  "&:hover": {
    "& span": {
      color: theme.palette.background.ffGreen,
    },
  },
});

// was: useStyles().notificationsFooter
const notificationsFooterSx = (theme) => ({
  "& a": {
    color: "#e9e9e9",
    textDecoration: "underline !important",
    "&:hover": {
      color: theme.palette.background.ffGreen,
    },
  },
});

// was: useStyles().avatarIcon
const avatarIconSx = {
  backgroundColor: "background.ffGreen",
  color: "background.ffNavy",
  fontWeight: 500,
};

// was: useStyles().warningIcon / .heavyWarningIcon
const warningIconSx = {
  color: "#f3c621",
  width: "1.2em",
  height: "1.2em",
  position: "absolute",
  top: "0",
  left: "-5px",
};

const heavyWarningIconSx = {
  ...warningIconSx,
  color: "#ff841b",
};

// was: useStyles().warningIconInline / .heavyWarningIconInline
const warningIconInlineSx = {
  color: "#f3c621",
  width: "1.2em",
  height: "1.2em",
  position: "absolute",
  top: "6px",
  marginLeft: "6px",
};

const heavyWarningIconInlineSx = {
  ...warningIconInlineSx,
  color: "#ff841b",
};

export default function HeaderInteral() {
  const { userProfile, logout } = useContext(AccountContext);
  const {
    overview,
    unreadNotificationCount,
    notificationsRead,
    isNewNotification,
  } = useContext(OverviewContext);
  const navigate = useNavigate();
  const theme = useTheme();

  // v4's <Hidden> was removed in v5+; the faithful port is `useMediaQuery` plus
  // a conditional render, because <Hidden>'s default "js" implementation
  // UNMOUNTED the subtree rather than hiding it with CSS.
  //
  // ♦ Both of this file's <Hidden>s are driven by ONE query on purpose. v4
  // resolved `xsDown` and `smUp` from a single `width` value, so the pair was
  // always exact complements — including when no media query could be evaluated
  // (jsdom, SSR/prerender), where v4's width fell through to "narrower than sm"
  // and rendered the mobile branch only. Two independent `useMediaQuery` calls
  // both default to `false` in that situation, which would render BOTH branches
  // (duplicate menu buttons in the prerendered HTML and in every jsdom test).
  // Deriving both booleans from `up("sm")` reproduces v4 at every width.
  //
  // API delta: v4's `down(key)` meant "up to the START of the NEXT breakpoint",
  // so `xsDown` === `max-width: 599.95px`; v5+ spells that `down("sm")`. Here
  // that query is exactly `!smUp`.
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  const hiddenXsDown = !smUp;
  const hiddenSmUp = smUp;

  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });

  const toggleDrawer = (side, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setState({ ...state, [side]: open });
  };

  const handleOpenNotificationsPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseNotificationsPopover = () => {
    setAnchorEl(null);
    return notificationsRead();
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const sideList = (side) => (
    // The v4 markup read `className={classes.list}`, but `list` was never a key
    // of this file's useStyles — the prop resolved to `undefined` and no class
    // was ever applied. Dropped rather than invented.
    <div
      role="presentation"
      onClick={toggleDrawer(side, false)}
      onKeyDown={toggleDrawer(side, false)}
    >
      {/* v9's Box reads `sx` only — the v4 shorthand system props
          (`display` / `alignItems` / `p` / `m`) and the v4-system `css` prop are
          forwarded to the DOM and apply nothing. Rewritten as sx keys with the
          same values: p/m stay spacing multiples (1 = 8px) and `height: 100`
          resolves to 100px in both systems. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          p: 1,
          m: 1,
          height: 100,
        }}
      >
        {userProfile && overview && (
          <Box style={{ width: "100%" }}>
            <List>
              <ListItem>
                <PersonRoundedIcon sx={mobileProfileButtonIconSx} />
                <Typography sx={mobileProfileNameSx}>
                  Hi {userProfile.firstName}!
                </Typography>
              </ListItem>
            </List>
            <Divider sx={mobileNavListDividerSx} />
            <List>
              {userProfile.memberStatus === "standard" && (
                <>
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemSubLink to="/videos">
                          Dashboard
                        </MobileNavListItemSubLink>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemSubLink to="/myaccount/overview">
                          My Account
                          {userProfile.subscriptionPaymentRetryCount > 0 && (
                            <WarningRoundedIcon
                              sx={
                                userProfile.subscriptionPaymentRetryCount === 1
                                  ? warningIconInlineSx
                                  : heavyWarningIconInlineSx
                              }
                            />
                          )}
                        </MobileNavListItemSubLink>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemSubLink to="/favourites">
                          My Favourites
                        </MobileNavListItemSubLink>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemSubLink to="/watch-history">
                          Watch History
                        </MobileNavListItemSubLink>
                      }
                    />
                  </ListItem>
                  {/* Release once App Issues Resolved */}
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemSubLink to="/inbox">
                          Notifications
                          {overview &&
                            overview.notifications &&
                            overview.notifications.length > 0 &&
                            unreadNotificationCount > 0 && (
                              <Badge
                                badgeContent={unreadNotificationCount}
                                color="error"
                                style={{ top: "-2px", right: "-18px" }}
                              ></Badge>
                            )}
                        </MobileNavListItemSubLink>
                      }
                    />
                  </ListItem>
                </>
              )}

              {userProfile.memberStatus === "trial" && (
                <>
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemLink
                          to="/myaccount/overview"
                          style={{ color: "#99ca3c" }}
                        >
                          Join Now
                        </MobileNavListItemLink>
                      }
                    />
                  </ListItem>
                  <Divider sx={mobileNavListDividerSx} />
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemSubLink to="/videos">
                          Dashboard
                        </MobileNavListItemSubLink>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemSubLink to="/myaccount/overview">
                          My Account{" "}
                        </MobileNavListItemSubLink>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemSubLink to="/favourites">
                          My Favourites
                        </MobileNavListItemSubLink>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemSubLink to="/watch-history">
                          Watch History
                        </MobileNavListItemSubLink>
                      }
                    />
                  </ListItem>
                  {/* Release once App Issues Resolved */}
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemSubLink to="/inbox">
                          Notifications
                          {overview &&
                            overview.notifications &&
                            overview.notifications.length > 0 &&
                            unreadNotificationCount > 0 && (
                              <Badge
                                badgeContent={unreadNotificationCount}
                                color="error"
                                style={{ top: "-2px", right: "-18px" }}
                              ></Badge>
                            )}
                        </MobileNavListItemSubLink>
                      }
                    />
                  </ListItem>
                </>
              )}
              {((userProfile.memberStatus === "standard-expired" &&
                !userProfile.productAutoRenewing) ||
                userProfile.memberStatus === "trial-expired") && (
                <>
                  <ListItem>
                    <ListItemText
                      primary={
                        <MobileNavListItemLink
                          to="/myaccount/overview"
                          style={{ color: "#99ca3c" }}
                        >
                          Join Now
                        </MobileNavListItemLink>
                      }
                    />
                  </ListItem>
                  <Divider sx={mobileNavListDividerSx} />
                </>
              )}

              {userProfile.memberStatus === "locked" &&
                overview.doors.open === true && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary={
                          <MobileNavListItemLink
                            to="/myaccount/overview"
                            style={{ color: "#99ca3c" }}
                          >
                            Join Now
                          </MobileNavListItemLink>
                        }
                      />
                    </ListItem>
                    <Divider sx={mobileNavListDividerSx} />
                  </>
                )}

              {userProfile.memberStatus === "new-trial-eligible" &&
                overview.doors.open === true && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary={
                          <MobileNavListItemLink
                            to="/myaccount/overview"
                            style={{ color: "#99ca3c" }}
                          >
                            Join Now
                          </MobileNavListItemLink>
                        }
                      />
                    </ListItem>
                    <Divider sx={mobileNavListDividerSx} />
                  </>
                )}

              {userProfile.memberStatus === "standard-incomplete" &&
                overview.doors.open === true && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary={
                          <MobileNavListItemLink
                            to="/myaccount/overview"
                            style={{ color: "#99ca3c" }}
                          >
                            Join Now
                          </MobileNavListItemLink>
                        }
                      />
                    </ListItem>
                    <Divider sx={mobileNavListDividerSx} />
                  </>
                )}

              {!(
                userProfile.memberStatus === MEMBER_STATUS_STANDARD ||
                userProfile.memberStatus === MEMBER_STATUS_TRIAL
              ) && (
                <ListItem>
                  <ListItemText
                    primary={
                      <MobileNavListItemSubLink to="/myaccount/overview">
                        My Account{" "}
                      </MobileNavListItemSubLink>
                    }
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemText
                  primary={
                    <MobileNavListItemSubAnchor
                      href="https://shop.farmflix.tv"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      The Shop
                    </MobileNavListItemSubAnchor>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <MobileNavListItemSubLink to="/help">
                      Help
                    </MobileNavListItemSubLink>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <MobileNavListItemSubLink to="/usemycode">
                      Redeem Giftcard
                    </MobileNavListItemSubLink>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <MobileNavListItemSubLink to="/contact">
                      Contact Us
                    </MobileNavListItemSubLink>
                  }
                />
              </ListItem>
              {overview && overview.doors?.careersOpen && (
                <ListItem>
                  <ListItemText
                    primary={
                      <MobileNavListItemSubLink to="/careers">
                        We Are Hiring
                      </MobileNavListItemSubLink>
                    }
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemText
                  primary={
                    <Typography
                      onClick={handleLogout}
                      sx={mobileNavListItemSubStyles}
                    >
                      Logout
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>
        )}
        <Grow />
        <Box sx={{ p: 1 }}>
          <CloseIcon
            sx={mobileNavCloseIconSx}
            onClick={toggleDrawer("right", true)}
          />
        </Box>
      </Box>
    </div>
  );

  if (!userProfile) return null;

  return (
    <AppBar position="static" color="inherit">
      <Toolbar>
        <Link to="/videos">
          <Logo src={logo} alt="FarmFLiX Logo" />
        </Link>
        <Grow />

        {/* Notifications List Popover */}
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseNotificationsPopover}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transitionDuration={{ enter: 250, exit: 0 }}
          sx={notificationsContainerSx}
        >
          <List sx={notificationsListSx}>
            {overview &&
              overview.notifications &&
              overview.notifications.length === 0 && (
                <>
                  <ListItem
                    sx={notificationWrapperSx}
                    style={{ textAlign: "center" }}
                  >
                    <ListItemText>
                      <NotificationsIcon sx={noNotificationsIconSx} />
                      <Typography sx={notificationTitleSx}>
                        No New Notifications
                      </Typography>
                    </ListItemText>
                  </ListItem>
                </>
              )}

            {overview &&
              overview.notifications &&
              overview.notifications.length > 0 && (
                <>
                  {overview.notifications.map((notification) => (
                    <React.Fragment key={notification.id}>
                      {/* No Link URL and No Video ID attached */}
                      {!notification.linkUrl &&
                        !notification.videoReferenceId && (
                          <Link to={"/inbox"}>
                            <ListItem sx={notificationWrapperSx}>
                              <ListItemText>
                                <Typography sx={notificationTitleSx}>
                                  {notification.title}
                                </Typography>
                                <Typography
                                  sx={notificationDescSx}
                                  variant="body1"
                                >
                                  {notification.message}
                                </Typography>
                              </ListItemText>
                              {isNewNotification(notification) && (
                                <NotificationUnreadIcon />
                              )}
                            </ListItem>
                          </Link>
                        )}
                      {/* External Link - No Video ID */}
                      {notification.linkUrl &&
                        !notification.videoReferenceId && (
                          <a
                            href={notification.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ListItem sx={notificationWrapperSx}>
                              <ListItemText>
                                <Typography sx={notificationTitleSx}>
                                  {notification.title}
                                </Typography>
                                <Typography
                                  sx={notificationDescSx}
                                  variant="body1"
                                >
                                  {notification.message}
                                </Typography>
                              </ListItemText>
                              {isNewNotification(notification) && (
                                <NotificationUnreadIcon />
                              )}
                            </ListItem>
                          </a>
                        )}
                      {/* Video ID - No External Link */}
                      {notification.videoReferenceId &&
                        !notification.linkUrl && (
                          <Link
                            to={
                              "/videos/" +
                              notification.tag +
                              "/" +
                              notification.videoReferenceId
                            }
                          >
                            <ListItem sx={notificationWrapperSx}>
                              <ListItemText>
                                <Typography sx={notificationTitleSx}>
                                  {notification.title}
                                </Typography>
                                <Typography
                                  sx={notificationDescSx}
                                  variant="body1"
                                >
                                  {notification.message}
                                </Typography>
                              </ListItemText>
                              {isNewNotification(notification) && (
                                <NotificationUnreadIcon />
                              )}
                            </ListItem>
                          </Link>
                        )}
                    </React.Fragment>
                  ))}
                  <Link to="/inbox">
                    <ListItem sx={notificationsViewAllSx}>
                      <ListItemText>View All Notifications</ListItemText>
                    </ListItem>
                  </Link>
                </>
              )}
            <ListItem sx={notificationsFooterSx}>
              <ListItemText>
                <Typography variant="body2">
                  You can{" "}
                  <a
                    href={"https://en-gb.facebook.com/farmflix/"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    follow us on Facebook
                  </a>{" "}
                  to receive the latest FarmFLiX News.
                </Typography>
              </ListItemText>
            </ListItem>
          </List>
        </Popover>

        {/* Tablet / Desktop — was <Hidden xsDown> */}
        {!hiddenXsDown && (
          <>
            {(userProfile.memberStatus === "standard" ||
              userProfile.memberStatus === "trial") && (
              <>
                <SearchModalIconButton />
                {overview &&
                  overview.notifications &&
                  overview.notifications.length === 0 && (
                    <Tooltip title="Notifications">
                      <IconButton
                        sx={notificationIconContainerSx}
                        onClick={handleOpenNotificationsPopover}
                      >
                        <NotificationsIcon sx={notificationButtonIconSx} />
                      </IconButton>
                    </Tooltip>
                  )}
                {overview &&
                  overview.notifications &&
                  overview.notifications.length > 0 && (
                    <>
                      <Tooltip title="Notifications">
                        <IconButton
                          sx={notificationIconContainerSx}
                          onClick={handleOpenNotificationsPopover}
                        >
                          {unreadNotificationCount > 0 && (
                            <Badge
                              badgeContent={unreadNotificationCount}
                              color="error"
                              sx={notificationBadgeSx}
                            >
                              <NotificationsIcon
                                sx={notificationButtonIconSx}
                              />
                            </Badge>
                          )}
                          {unreadNotificationCount === 0 && (
                            <NotificationsIcon sx={notificationButtonIconSx} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
              </>
            )}

            <Tooltip title="Open Menu">
              <IconButton
                name="openMenu"
                sx={profileIconContainerSx}
                onClick={toggleDrawer("right", true)}
                disableRipple={true}
              >
                <Badge
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  badgeContent={
                    <>
                      {userProfile.subscriptionPaymentRetryCount > 0 && (
                        <WarningRoundedIcon
                          sx={
                            userProfile.subscriptionPaymentRetryCount === 1
                              ? warningIconSx
                              : heavyWarningIconSx
                          }
                        />
                      )}
                    </>
                  }
                >
                  <Avatar sx={avatarIconSx}>
                    {userProfile.firstName.charAt(0).toLocaleUpperCase()}
                  </Avatar>
                </Badge>
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Mobile — was <Hidden smUp> */}
        {!hiddenSmUp && (
          <>
            {(userProfile.memberStatus === "standard" ||
              userProfile.memberStatus === "trial") && (
              <>
                <SearchModalIconButton />
                {overview &&
                  overview.notifications &&
                  overview.notifications.length === 0 && (
                    <>
                      <IconButton
                        sx={iconContainerSx}
                        onClick={handleOpenNotificationsPopover}
                      >
                        <NotificationsIcon sx={buttonIconSx} />
                      </IconButton>
                    </>
                  )}

                {overview &&
                  overview.notifications &&
                  overview.notifications.length > 0 && (
                    <Tooltip title="Notifications">
                      <IconButton
                        sx={iconContainerSx}
                        onClick={handleOpenNotificationsPopover}
                      >
                        {unreadNotificationCount > 0 && (
                          <Badge
                            badgeContent={unreadNotificationCount}
                            color="error"
                            sx={notificationBadgeSx}
                          >
                            <NotificationsIcon sx={buttonIconSx} />
                          </Badge>
                        )}
                        {unreadNotificationCount === 0 && (
                          <NotificationsIcon sx={notificationButtonIconSx} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
              </>
            )}

            <Tooltip title="Open Menu">
              <IconButton
                name="openMenu"
                sx={iconContainerSx}
                style={{ paddingRight: "0" }}
                onClick={toggleDrawer("right", true)}
              >
                <Badge
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  badgeContent={
                    <>
                      {userProfile.subscriptionPaymentRetryCount > 0 && (
                        <WarningRoundedIcon
                          sx={
                            userProfile.subscriptionPaymentRetryCount === 1
                              ? warningIconSx
                              : heavyWarningIconSx
                          }
                        />
                      )}
                    </>
                  }
                >
                  <Avatar sx={avatarIconSx}>
                    {userProfile.firstName.charAt(0).toLocaleUpperCase()}
                  </Avatar>
                </Badge>
              </IconButton>
            </Tooltip>
          </>
        )}
      </Toolbar>

      {/* Two Drawers, both bound to `state.right` and both rendering the same
          sideList, is pre-existing behaviour — kept verbatim (epic rule 3).
          The first one's v4 `classes={{ paper: classes.mobileNav }}` referenced
          a key this file never defined, so it resolved to `{ paper: undefined }`
          and applied nothing; that no-op prop is dropped rather than invented. */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={state.right}
        onClose={toggleDrawer("right", false)}
      >
        {sideList("right")}
      </Drawer>

      <Drawer
        variant="temporary"
        anchor="right"
        slotProps={{ paper: { sx: sideNavPaperSx } }}
        open={state.right}
        onClose={toggleDrawer("right", false)}
      >
        {sideList("right")}
      </Drawer>
    </AppBar>
  );
}
